import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { getBinClampedSize } from '@kb3d/common-util/texture-util';
import { eTextureChannel } from '@kb3d/models/classes';
import { DisplacementMapFeature } from '@kb3d/models/classes/features/displacement-map-feature';
import { Token } from '@kb3d/models/classes/token';
import { SubmeshVertexData } from '@kb3d/view/helpers/geometry-helper';
import { FeatureRenderer, SubmeshVertexDataMap } from './feature';
import { ensureNormalsExist } from './utils';
import {
    DisplacementMapGeometryProcessor,
    DisplacementMapWorkerPayload,
} from './geometry-processor/displacement-map-geometry';

export class DisplacementMapRenderer extends FeatureRenderer<DisplacementMapFeature, DisplacementMapWorkerPayload> {
    modelToken = Token.DisplacementMapFeature;
    textureCanvas = document.createElement('canvas');

    processor = new DisplacementMapGeometryProcessor(this.scene);

    async makeWorkerPayload(feature: DisplacementMapFeature, geometryData: SubmeshVertexDataMap) {
        const payload = await super.makeWorkerPayload(feature, geometryData);
        const textureRenderer = this.viewer.rendererManager.textures;
        if (!feature.textures || feature.textures.length === 0) {
            return payload;
        }

        //create context
        const context = this.textureCanvas.getContext('2d', {
            willReadFrequently: true,
        });
        const strength = 1;
        const hasTransparency = false;

        const textureLayers = textureRenderer.getActiveLayers(feature, eTextureChannel.albedo).filter(node => {
            const renderer = textureRenderer.getRenderer(node);
            return renderer.textureHasContent(node);
        });

        if (!context || textureLayers.length === 0) {
            return payload;
        }

        const baseImageSize = textureRenderer.getRenderer(feature.textures[0]).getNativeSize(feature.textures[0]);
        const parameters = { strength, hasTransparency, nativeSize: baseImageSize };
        
        if (this.scene.getEngine().webGLVersion < 2) {
            const clampedSize = getBinClampedSize(baseImageSize.width, baseImageSize.height);
            context.canvas.width = clampedSize;
            context.canvas.height = clampedSize;
        } else {
            if (feature.textures[0]) {
                context.canvas.width = baseImageSize.width;
                context.canvas.height = baseImageSize.height;
            }
        }
        context.clearRect(0, 0, context.canvas.width, context.canvas.height);

        const paintedImage = textureRenderer.paintTextureOnCanvas(textureLayers, context, parameters);

        const imageData = paintedImage.getImageData(0, 0, context.canvas.width, context.canvas.height);

        // const buffer = await new Promise<ArrayBuffer | null>(resolve => {
        //     try {
        //         paintedImage.canvas.toBlob(blob => {
        //             if (!blob) {
        //                 console.warn(`Failed to convert canvas to blob for feature ${feature.name}`);
        //                 resolve(null);
        //                 return;
        //             }

        //             blob.arrayBuffer().then(buffer => {
        //                 resolve(buffer);
        //             });
        //         });
        //     } catch (e) {
        //         console.warn(`Failed to convert canvas to blob for feature ${feature.name}: ${e}`);
        //         resolve(null);
        //     }
        // });

        // if (!buffer) {
        //     return payload;
        // }

        const featureData: DisplacementMapWorkerPayload = {
            textureWidth: context.canvas.width,
            textureHeight: context.canvas.height,
            textureImageData: imageData.data,
        };

        payload.featureData = featureData;
        payload.transfer = [imageData.data.buffer];

        return payload;
    }

    updateMeshGeometry(
        feature: DisplacementMapFeature,
        vertexData: SubmeshVertexData,
        vertexDataChanges: Partial<SubmeshVertexData>
    ) {
        const newVertexData = super.updateMeshGeometry(feature, vertexData, vertexDataChanges);
        const textureRenderer = this.viewer.rendererManager.textures;
        if (feature.textures && feature.textures.length > 0) {
            //create context
            const context = this.textureCanvas.getContext('2d', {
                willReadFrequently: true,
            });
            const strength = 1;
            const hasTransparency = false;
            
            const textureLayers = textureRenderer.getActiveLayers(feature, eTextureChannel.albedo).filter(node => {
                const renderer = textureRenderer.getRenderer(node);
                return renderer.textureHasContent(node);
            });

            if (context) {
                const baseImageSize = textureRenderer
                    .getRenderer(feature.textures[0])
                    .getNativeSize(feature.textures[0]);
                const parameters = { strength, hasTransparency, nativeSize: baseImageSize };
                
                if (this.scene.getEngine().webGLVersion < 2) {
                    const clampedSize = getBinClampedSize(baseImageSize.width, baseImageSize.height);
                    context.canvas.width = clampedSize;
                    context.canvas.height = clampedSize;
                } else {
                    if (feature.textures[0]) {
                        context.canvas.width = baseImageSize.width;
                        context.canvas.height = baseImageSize.height;
                    }
                }
                context.clearRect(0, 0, context.canvas.width, context.canvas.height);

                const paintedImage = textureRenderer.paintTextureOnCanvas(textureLayers, context, parameters);

                const tempVertexData = new SubmeshVertexData();
                tempVertexData.copyFrom(newVertexData);
                if (tempVertexData.uvs) {
                    const n = tempVertexData.uvs.length;
                    for (let i = 1; i < n; i += 2) {
                        tempVertexData.uvs[i] = 1 - tempVertexData.uvs[i];
                    }

                    const meshUpdated = new Mesh('ExistingMesh');
                    tempVertexData.applyToMesh(meshUpdated, true);
                    ensureNormalsExist(meshUpdated);

                    const imageData = paintedImage?.getImageData(
                        0,
                        0,
                        this.textureCanvas.width,
                        this.textureCanvas.height
                    );
                    if (imageData) {
                        this.displace(imageData, feature.minDisplacement, feature.maxDisplacement, meshUpdated);
                    }
                    const meshVertexData = VertexData.ExtractFromMesh(meshUpdated);
                    newVertexData.positions = meshVertexData.positions;
                    newVertexData.normals = meshVertexData.normals;
                    meshUpdated.dispose();
                }
            }
        }
        return newVertexData;
    }

    update3dInternal() {}

    displace(context: ImageData, minDisplacement: number, maxDisplacement: number, parentMesh: Mesh) {
        const uInt8Array = new Uint8Array(context.data);
        parentMesh.applyDisplacementMapFromBuffer(
            uInt8Array,
            context.width,
            context.height,
            minDisplacement,
            maxDisplacement
        );
    }
}
